import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { AuthModal } from "@core/components/modals/AuthModal";
import { setAuthReturnTo } from "@core/lib/authReturnTo";

interface AuthRequiredProps {
  children: React.ReactNode;
  /**
   * Optional header rendered above the gating UI when the user is logged out.
   * Lets the visitor navigate elsewhere instead of being trapped on the gate.
   */
  header?: React.ReactNode;
}

/**
 * Composant wrapper qui bloque l'accès aux utilisateurs non connectés
 * Affiche un message invitant à se connecter si l'utilisateur n'est pas authentifié
 */
export const AuthRequired: React.FC<AuthRequiredProps> = ({
  children,
  header,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");

  console.log("🔒 [AUTH_REQUIRED] Checking authentication", {
    hasUser: !!user,
    userId: user?.id,
    userEmail: user?.email,
  });

  if (!user) {
    console.log("❌ [AUTH_REQUIRED] No user - blocking access");

    // Sauvegarder l'URL actuelle pour redirection post-auth
    const handleOpenAuth = (mode: "signin" | "signup") => {
      const currentPath = window.location.pathname;
      console.log(
        "💾 [AUTH_REQUIRED] Saving current path for post-auth redirect:",
        currentPath,
      );
      setAuthReturnTo(currentPath);
      setAuthMode(mode);
      setAuthModalOpen(true);
    };

    return (
      <div className="flex flex-col min-h-screen-safe">
        {header}
        <div className="flex-1 bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card rounded-2xl shadow-xl p-8 text-center border border-border">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>

            <h1 className="text-2xl font-bold text-foreground mb-4">
              {t("auth_required_title")}
            </h1>

            <p className="text-muted-foreground mb-8">
              {t("auth_required_description")}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => handleOpenAuth("signin")}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {t("auth:sign_in")}
              </button>

              <button
                onClick={() => navigate("/")}
                className="w-full border-2 border-border hover:border-border/80 text-muted-foreground hover:text-foreground font-semibold py-3 px-6 rounded-lg transition-all duration-200"
              >
                {t("back_home")}
              </button>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              {t("auth_required_no_account")}{" "}
              <button
                onClick={() => handleOpenAuth("signup")}
                className="text-primary hover:underline font-medium"
              >
                {t("auth_required_create_account")}
              </button>
            </p>
          </div>
        </div>

        {/* Modal d'authentification */}
        <AuthModal
          open={authModalOpen}
          onOpenChange={setAuthModalOpen}
          defaultMode={authMode}
        />
      </div>
    );
  }

  return <>{children}</>;
};
